/**
 * Snapshot and restore of the live canvas around a preview session.
 *
 * The snapshot is keyed by object identity (WeakMap + entry list), not by
 * `__uid`, so restore is correct even if two objects share an id. Each frame
 * is resolved from the snapshot, never from the object, otherwise frame N+1
 * would compound frame N's deltas.
 */

import type { fabric } from 'fabric';
import type { LayerFrameState } from '@canvas/shared-types';
import { LAYER_CUSTOM_PROPS } from '@/lib/layerOps';
import { applyLayerState, readFrameState } from './layerAnimation';

export interface ObjectBaseline extends LayerFrameState {
  objectCaching: boolean;
  evented: boolean;
}

export interface BaselineSnapshot {
  entries: Array<[fabric.Object, ObjectBaseline]>;
  byObject: WeakMap<fabric.Object, ObjectBaseline>;
  selection: boolean;
  skipTargetFind: boolean;
}

function captureObject(obj: fabric.Object): ObjectBaseline {
  return {
    ...readFrameState(obj),
    objectCaching: obj.objectCaching !== false,
    evented: obj.evented !== false,
  };
}

/** Capture every top-level object's authored transform and interaction flags. */
export function captureBaseline(canvas: fabric.Canvas): BaselineSnapshot {
  const snapshot: BaselineSnapshot = {
    entries: [],
    byObject: new WeakMap(),
    selection: canvas.selection !== false,
    skipTargetFind: Boolean(canvas.skipTargetFind),
  };
  canvas.getObjects().forEach((obj) => addToBaseline(snapshot, obj));
  return snapshot;
}

/** Add an object that appeared mid-session so it is restored too. */
export function addToBaseline(snapshot: BaselineSnapshot, obj: fabric.Object): ObjectBaseline {
  const existing = snapshot.byObject.get(obj);
  if (existing) return existing;
  const base = captureObject(obj);
  snapshot.entries.push([obj, base]);
  snapshot.byObject.set(obj, base);
  return base;
}

/** Put every captured object and the canvas flags back exactly as captured. */
export function restoreBaseline(canvas: fabric.Canvas, snapshot: BaselineSnapshot): void {
  for (const [obj, base] of snapshot.entries) {
    applyLayerState(obj, base);
    obj.objectCaching = base.objectCaching;
    obj.evented = base.evented;
    obj.setCoords();
  }
  canvas.selection = snapshot.selection;
  canvas.skipTargetFind = snapshot.skipTargetFind;
}

/** Serialized canvas, for the DEV-only restore assertion. */
export function baselineJson(canvas: fabric.Canvas): string {
  return JSON.stringify(canvas.toJSON([...LAYER_CUSTOM_PROPS]));
}

/** Describe the first difference between two baselineJson() strings. */
export function describeBaselineDiff(before: string, after: string): string {
  try {
    const a = JSON.parse(before) as { objects?: Array<Record<string, unknown>> };
    const b = JSON.parse(after) as { objects?: Array<Record<string, unknown>> };
    const objsA = a.objects ?? [];
    const objsB = b.objects ?? [];
    if (objsA.length !== objsB.length) {
      return `object count ${objsA.length} → ${objsB.length}`;
    }
    for (let i = 0; i < objsA.length; i += 1) {
      const keys = new Set([...Object.keys(objsA[i]), ...Object.keys(objsB[i])]);
      for (const key of keys) {
        if (JSON.stringify(objsA[i][key]) !== JSON.stringify(objsB[i][key])) {
          const uid = String(objsA[i].__uid ?? `#${i}`);
          return `${uid}.${key}: ${JSON.stringify(objsA[i][key])} → ${JSON.stringify(objsB[i][key])}`;
        }
      }
    }
    return 'canvas-level property';
  } catch {
    return 'unparseable';
  }
}
